import { rowMatchesTaxYear } from "./taxYear.js";
import { pickSectionTotals } from "./fiatFields.js";
import { nftRowClass } from "./nftCostBasis.js";
import {
  buildNftTaxTableDisplayRows,
  isNftTaxPrimaryDisplayRow,
} from "../../shared/nftAcquisitionLedger.js";

function buildTotalsFromMeta(totalsFromProps, selectedCountry, extra = {}) {
  const m = pickSectionTotals(totalsFromProps || {}, selectedCountry);

  return {
    swapProfit: m.disposalNet,
    swapGain: m.disposalGain,
    swapLoss: m.disposalLoss,
    otherIncome: m.otherIncome,
    totalTaxable: m.totalTaxable,
    disposalShortTermNetFiat: m.disposalShortTermNet,
    disposalLongTermNetFiat: m.disposalLongTermNet,
    ...extra,
  };
}

/**
 * Synchron aus Props — für PDF/Print ohne useEffect-Race.
 * @param {{ filterRow?: Function, filterRows?: Function }} options
 *   filterRow(tx, year, country) → boolean (pro Zeile)
 *   filterRows(rows, year, country) → rows[] (Batch, z. B. VTHO-Rollup)
 */
export function buildTaxTableSectionData(
  rowsFromProps,
  totalsFromProps,
  effectiveYear,
  selectedCountry,
  options = {}
) {
  const { filterRow, filterRows } = options;
  let yearRows = [];

  if (typeof filterRows === "function") {
    yearRows = filterRows(rowsFromProps || [], effectiveYear, selectedCountry);
  } else {
    const source = Array.isArray(rowsFromProps) ? rowsFromProps : [];
    yearRows = source.filter((tx) => {
      if (!tx?.timestamp) return false;
      if (!effectiveYear) return true;

      if (typeof filterRow === "function") {
        return filterRow(tx, effectiveYear, selectedCountry);
      }

      return rowMatchesTaxYear(tx, selectedCountry, Number(effectiveYear));
    });
  }

  return {
    rows: yearRows,
    totals: buildTotalsFromMeta(totalsFromProps, selectedCountry),
  };
}

/** VTHO: inkl. Generated-Rollup — filterFn(rows, year, country) → rows[] */
export function buildVthoTaxTableSectionData(
  rowsFromProps,
  totalsFromProps,
  effectiveYear,
  selectedCountry,
  filterFn
) {
  return buildTaxTableSectionData(
    rowsFromProps,
    totalsFromProps,
    effectiveYear,
    selectedCountry,
    typeof filterFn === "function" ? { filterRows: filterFn } : {}
  );
}

/** NFT: one row per NFT identity (batch parents expanded). */
export function buildNftTaxTableSectionData(
  nftDataFromProps,
  nftTotalsFromProps,
  effectiveYear,
  selectedCountry,
  options = {}
) {
  if (!Array.isArray(nftDataFromProps)) {
    return { rows: [], totals: { totalCost: 0, totalValue: 0 } };
  }

  const taxRows = nftDataFromProps
    .map((r) => {
      let cls = nftRowClass(r);
      if (cls === "mint") cls = "nft_mint";
      if (cls === "sale") cls = "sale_payment";
      if (cls === "galaxy_mint") cls = "nft_galaxy_mint";
      return {
        ...r,
        rawClassification: r.classification,
        classification: cls || r.classification,
      };
    })
    .filter(isNftTaxPrimaryDisplayRow)
    .filter((r) => {
      const timestamp =
        nftRowClass(r) === "nft_transform"
          ? r.nftTransformBurnTs || r.timestamp || r.nftTransformMintTs
          : r.timestamp || r.nftTransformBurnTs || r.nftTransformMintTs;
      if (!timestamp) return false;
      if (!effectiveYear) return false;
      return rowMatchesTaxYear(
        { ...r, timestamp },
        selectedCountry,
        Number(effectiveYear)
      );
    });

  const acquisitionRows = (
    Array.isArray(options.acquisitionRows) ? options.acquisitionRows : []
  ).filter((r) => {
    if (!r.timestamp && !r.mintTimestamp && !r.paymentTimestamp) return false;
    if (!effectiveYear) return false;
    return rowMatchesTaxYear(
      {
        ...r,
        timestamp: r.timestamp || r.mintTimestamp || r.paymentTimestamp,
      },
      selectedCountry,
      Number(effectiveYear)
    );
  });

  const rows = buildNftTaxTableDisplayRows(taxRows, acquisitionRows);

  const m = pickSectionTotals(nftTotalsFromProps || {}, selectedCountry);

  return {
    rows,
    totals: {
      disposalGainEUR: m.disposalGain,
      disposalLossEUR: m.disposalLoss,
      disposalNetEUR: m.disposalNet,
      disposalEUR: m.disposalNet,
      incomeEUR: m.otherIncome,
      totalCost: m.disposalNet,
      totalValue: m.disposalNet,
      disposalShortTermNetFiat: m.disposalShortTermNet,
      disposalLongTermNetFiat: m.disposalLongTermNet,
    },
  };
}
